import { ulid } from 'ulid';
import { z } from 'zod';
import i18nSupported from './i18n-supported.json';

export const ID_PREFIXES = {
  household: 'hsh',
  user: 'usr',
  staffProfile: 'stf',
  session: 'ses',
  ownerInvite: 'inv',
  staffInvite: 'sti',
  // Password reset AND email verification — one table, told apart by
  // `purpose` (migration 0075).
  passwordResetToken: 'prt',
  operation: 'op',
  operationStateHistory: 'osh',
  operationUpdate: 'upd',
  operationProof: 'prf',
  operationLink: 'olk',
  routine: 'rt',
  routineEmission: 'rte',
  memoryEntry: 'mem',
  // A note somebody wrote for themselves. Not 'nty' (notification) and not
  // 'mem' (a house rule everyone reads) — nobody but the author ever sees one.
  note: 'nte',
  receipt: 'rcp',
  receiptScan: 'rcs',
  reimbursement: 'rmb',
  loan: 'loa',
  loanRepayment: 'lrp',
  cashRequest: 'csh',
  expectedBill: 'ebl',
  compensation: 'cmp',
  workEntry: 'wke',
  shift: 'shf',
  // The planned half of attendance — who is supposed to work when — as
  // opposed to `shift`, which is the clock-in that actually happened.
  scheduledShift: 'sch',
  operationWorkSession: 'ows',
  payrollAdjustment: 'adj',
  payrollRun: 'pyr',
  payslip: 'psl',
  calendarEvent: 'cal',
  notification: 'nty',
  pushSubscription: 'psb',
  pushDeviceToken: 'pdt',
  aiLog: 'ail',
  auditLog: 'aud',
  suggestion: 'sug',
  // Standalone staff → owner: nothing to attach to, unlike every other
  // write an assignee can make.
  staffMessage: 'stm',
  // The list we sent someone over WhatsApp, and every message either way.
  // These carry the record that settles "I told her" against "nobody told me".
  // One row per (household, worker, domain) in the worker-improvement loop —
  // migration 0051, apps/api/src/learning/recorder.ts.
  workerDomainCompetence: 'wdc',
  whatsappThread: 'wat',
  whatsappMessage: 'wam',
  // The Household OS graph (Wave 1, Household Identity): typed members and
  // the relationships between them.
  householdMember: 'hmm',
  householdMemberRelationship: 'hmr',
  // The intelligence packs' idempotence ledger (Wave 2, migration 0054).
  packSeed: 'pks',
  researchExperiment: 'exp',
  experimentAssignment: 'exa',
  researchConsent: 'cns',
  productEvent: 'pev',
  journeyEligibility: 'jel',
  interventionAnnotation: 'ian',
  ownerEffortSample: 'oes',
  pilotAccessGrant: 'pag',
  pilotIntent: 'pit',
  annotationCase: 'anc',
  annotationRevision: 'anr',
  annotationAdjudication: 'adjc',
  operationalChain: 'chn',
  chainRevision: 'crv',
  chainAction: 'cac',
  chainDependency: 'cdp',
  chainWait: 'cwt',
  chainObligation: 'cob',
  chainDecision: 'cdc',
  chainOutcome: 'cou',
  chainPlannerRun: 'cpr',
  chainPlannerCorrection: 'cpc',
  contextSnapshot: 'ctx',
  contextCandidate: 'ctc',
  contextDecisionUse: 'ctu',
  contextMemoryCandidate: 'cmc',
  contextCandidateEvent: 'cce',
  contextConflict: 'ccf',
  contextDecisionEffect: 'cde',
  chainGapDecision: 'cgd',
  safeDefault: 'sdf',
  clarificationQuestion: 'clq',
  clarificationAnswer: 'cla',
  clarificationEffect: 'cle',
  workerSupportProfile: 'wsp',
  workerNextActionTrace: 'wnt',
  workerSupportResponse: 'wsr',
  workerActionRendering: 'war',
  workerDeliveryAttempt: 'wda',
  workerSupportEvent: 'wse',
  obligationOutbox: 'oob',
  obligationInbox: 'oin',
  obligationAttempt: 'oat',
  obligationEvent: 'oev',
  ownerAttentionItem: 'oai',
  outcomeEvaluation: 'oev2',
  outcomeOverride: 'oov',
  outcomeCorrection: 'ocr',
  // The usage ledger (migration 0082): one row per business event counted
  // against a household's published monthly allowance.
  usageEvent: 'usg',
  // The Household Vault (migrations 0083-0084). A document is the thing a
  // person names; a version is the immutable bytes. They are separate
  // entities, and separate prefixes, because every "save" creates a version
  // and none of them ever replaces another.
  document: 'doc',
  documentVersion: 'dvr',
  documentShare: 'dsh',
  documentLink: 'dlk',
  documentAccessEvent: 'dae',
  documentDeletionRequest: 'ddr',
  storageGrant: 'sgr',
  storageReservation: 'srv',
  storageLedgerEntry: 'sul',
  // Provider-neutral billing (migration 0085). billing_catalog is keyed by its
  // logical SKU and billing_accounts by the household, so neither appears
  // here — a table whose key is meaningful does not want a generated one.
  billingProviderProduct: 'bpp',
  billingPurchase: 'bpu',
  billingPurchaseItem: 'bpi',
  billingEvent: 'bev',
  // Consented temporal research (migration 0086). The subject row is the only
  // place a pseudonym maps back to a person.
  temporalResearchSubject: 'trs',
  temporalParseExample: 'tpe',
  // The founder-curated general-knowledge corpus (docs/product/assistant-
  // composer.md, "The knowledge base"). knowledge_notes is global — no
  // household_id — so this is the only key namespacing its rows.
  knowledgeNote: 'kno',
  // Cross-household pattern learning, Layer 1 (a household's own minimized
  // contribution). Layer 2 (cross_household_aggregates) is keyed by domain,
  // not a generated id, same reasoning as billing_catalog above.
  crossHouseholdContribution: 'chc',
  // Wave 1 — situation engine + household graph (01-data-model.md §0/§1/§2,
  // packages/db/src/schema/situations.ts + entities.ts). The SoT documents
  // these ids as plain ULIDs (§0), but every other table in the repo mints
  // through this registry, so the Wave-1 tables get prefixes here too rather
  // than a second, parallel id-minting convention (WS-2.2 persistence repos).
  situation: 'sit',
  situationEvent: 'sev',
  situationOption: 'sop',
  situationObligation: 'sob',
  commitment: 'cmt',
  entity: 'ent',
  entityRelation: 'erl',
  temporalFact: 'tfc',
  // Wave 2 — the trust ladder + money gate (packages/db/src/schema/trust.ts)
  // and the append-only ledger (packages/db/src/schema/ledgers.ts).
  trustProfile: 'trp',
  autonomyEvent: 'aev',
  moneyPermissionRule: 'mpr',
  ledgerAccount: 'lac',
  ledgerEntry: 'len',
  // The conversational composer's chat history (migration 0101,
  // packages/db/src/schema/composer-messages.ts).
  composerMessage: 'cps',
} as const;

export type EntityKey = keyof typeof ID_PREFIXES;

export function newId<K extends EntityKey>(entity: K): `${(typeof ID_PREFIXES)[K]}_${string}` {
  return `${ID_PREFIXES[entity]}_${ulid()}` as `${(typeof ID_PREFIXES)[K]}_${string}`;
}

export function idSchema<K extends EntityKey>(entity: K) {
  const prefix = ID_PREFIXES[entity];
  return z.string().regex(new RegExp(`^${prefix}_[0-9A-HJKMNP-TV-Z]{26}$`), {
    message: `expected ${entity} id (${prefix}_…)`,
  });
}

/**
 * A validator for EVERY entity in `ID_PREFIXES`, derived from the registry
 * itself.
 *
 * Only about a third of the entities had a hand-written `*Id` export, so the
 * other seventy — every chain, context, obligation, clarification and outcome
 * id — crossed route boundaries as bare `z.string()`. That is not a cosmetic
 * gap: a bare string accepts another entity's id, so a handler can be handed a
 * `cac_…` where it expects a `chn_…` and only find out when a foreign key
 * fails, or worse, when it doesn't.
 *
 * Generated rather than listed so the gap cannot reopen: adding a prefix to
 * `ID_PREFIXES` now adds its validator in the same edit. The named exports
 * below are kept because they are what the codebase already imports, and they
 * are drawn from here so there is exactly one schema per entity.
 */
export const EntityId = Object.fromEntries(
  (Object.keys(ID_PREFIXES) as EntityKey[]).map((entity) => [entity, idSchema(entity)]),
) as { [K in EntityKey]: z.ZodString };

export const HouseholdId = EntityId.household;
export const UserId = EntityId.user;
export const SessionId = EntityId.session;
export const OwnerInviteId = EntityId.ownerInvite;
export const StaffInviteId = EntityId.staffInvite;
export const OperationId = EntityId.operation;
export const OperationUpdateId = EntityId.operationUpdate;
export const OperationProofId = EntityId.operationProof;
export const RoutineId = EntityId.routine;
export const MemoryEntryId = EntityId.memoryEntry;
export const NoteId = EntityId.note;
export const ReceiptId = EntityId.receipt;
export const ReceiptScanId = EntityId.receiptScan;
export const ReimbursementId = EntityId.reimbursement;
export const LoanId = EntityId.loan;
export const CashRequestId = EntityId.cashRequest;
export const StaffMessageId = EntityId.staffMessage;
export const ExpectedBillId = EntityId.expectedBill;
export const CompensationId = EntityId.compensation;
export const WorkEntryId = EntityId.workEntry;
export const ScheduledShiftId = EntityId.scheduledShift;
export const PayrollAdjustmentId = EntityId.payrollAdjustment;
export const PayrollRunId = EntityId.payrollRun;
export const PayslipId = EntityId.payslip;
export const CalendarEventId = EntityId.calendarEvent;
export const NotificationId = EntityId.notification;
export const AiLogId = EntityId.aiLog;
export const AuditLogId = EntityId.auditLog;
export const SuggestionId = EntityId.suggestion;
export const HouseholdMemberId = EntityId.householdMember;
export const HouseholdMemberRelationshipId = EntityId.householdMemberRelationship;

export type HouseholdId = z.infer<typeof HouseholdId>;
export type UserId = z.infer<typeof UserId>;
export type OperationId = z.infer<typeof OperationId>;
export type ReceiptId = z.infer<typeof ReceiptId>;
export type ReceiptScanId = z.infer<typeof ReceiptScanId>;
export type ReimbursementId = z.infer<typeof ReimbursementId>;
export type LoanId = z.infer<typeof LoanId>;

// The language model is data-driven, never a hand-maintained list: every
// language the mobile app has a pack for (famanage-mobile i18n packs, 51
// today) plus 'en' (the source language) and 'tl' (a legacy built-in
// overlay) — both built-in, neither with a pack file. i18n-supported.json
// is a committed artifact regenerated by apps/api/scripts/sync-i18n-packs.mjs
// from the mobile packs manifest; do not edit it by hand. Existing rows
// (en/he/ur/tl) are all inside this set, so the widening is purely additive.
export const SUPPORTED_LANG_CODES: readonly string[] = i18nSupported.supportedLangs;
export const LangCode = z.enum(SUPPORTED_LANG_CODES as [string, ...string[]]);
export type LangCode = z.infer<typeof LangCode>;

export const UserRole = z.enum(['owner', 'staff']);
export type UserRole = z.infer<typeof UserRole>;

export const StaffPosition = z.enum([
  'driver',
  'personal_assistant',
  'housemaid',
  'gardener',
  'nanny',
  'cook',
  'other',
]);
export type StaffPosition = z.infer<typeof StaffPosition>;

/**
 * An ISO 4217 alphabetic code.
 *
 * `z.string().length(3)` accepted `"12$"`, `"   "` and `"aed"` — on the type
 * that sits inside `Money` and on every payslip, loan and reimbursement. A
 * currency that is three characters of anything is not a currency, and the
 * damage is silent: it stores, it renders, and it never equals the code the
 * rest of the household's money is denominated in.
 *
 * Trimmed and upper-cased before the shape check, the same way `Email` folds
 * case at the boundary: ISO 4217 codes are conventionally upper-case, so
 * `"aed"` is a formatting difference rather than a different currency, and
 * canonicalising here means no comparison downstream has to remember that.
 *
 * Deliberately a pattern and not an embedded list of the ~180 active codes.
 * A list is only a safety improvement while it is CURRENT — ISO 4217 changes
 * (RUR→RUB, the 2024 additions), and a stale copy vendored into a schemas
 * package rejects a household's real currency with no way for them to route
 * around it. The pattern rejects everything that is definitely not a currency
 * code, which is the actual bug being fixed; validating that a well-formed
 * code is also an ISSUED one belongs with data that can be refreshed.
 */
export const Currency = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'ISO 4217 currency expected (three letters, e.g. AED)');
export type Currency = z.infer<typeof Currency>;

export const Money = z.object({
  amount_minor: z.number().int(),
  currency: Currency,
});
export type Money = z.infer<typeof Money>;

export const IsoDatetime = z.string().datetime({ offset: true });

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Checked arithmetically rather than by round-tripping through `Date`:
 * `new Date('2026-02-31')` rolls forward to March 3rd instead of failing, and
 * `Date.UTC` maps a two-digit year into the 1900s — both turn a validator into
 * a silent corrector.
 */
function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const max = month === 2 && isLeap ? 29 : (DAYS_IN_MONTH[month - 1] as number);
  return day <= max;
}

/**
 * A real calendar date, not merely a digit pattern.
 *
 * The shape check alone accepted `9999-99-99` and `2026-02-30`. These are
 * payroll period bounds, work-entry dates and deduction start dates — an
 * impossible one is accepted at the boundary, stored, and then either silently
 * shifts (Postgres and `Date` both prefer to reinterpret rather than refuse)
 * or blows up somewhere far from the request that introduced it.
 */
export const IsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD expected')
  .refine((value) => {
    const [year, month, day] = value.split('-').map(Number) as [number, number, number];
    return isRealCalendarDate(year, month, day);
  }, 'not a real calendar date');

export const HouseholdCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z2-9]{4}-[A-Z2-9]{2}$/, 'expected APXY-7K format');
export type HouseholdCode = z.infer<typeof HouseholdCode>;

export const Username = z
  .string()
  .min(3)
  .max(24)
  .regex(/^[a-z0-9._]+$/, 'lowercase letters, digits, dot, underscore');
export type Username = z.infer<typeof Username>;

// Trimmed and lowercased before validation: an email address is
// case-insensitive as an identity, and folding at the schema boundary keeps
// every write and lookup canonical without each route remembering to.
export const Email = z.string().trim().toLowerCase().email().max(254);
export const PhoneE164 = z.string().regex(/^\+[1-9]\d{6,14}$/);

/**
 * What the unified one-form sign-in accepts as `identifier`: an owner email,
 * a staff username, or a staff phone in E.164. Trimmed first so padded input
 * from a mobile keyboard resolves exactly the way it did when the route took
 * a free-form string and trimmed in the service.
 */
export const SignInIdentifier = z
  .string()
  .trim()
  .pipe(z.union([Email, Username, PhoneE164]));
export type SignInIdentifier = z.infer<typeof SignInIdentifier>;

export const PageParams = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(20),
});
export type PageParams = z.infer<typeof PageParams>;

/**
 * The querystring half of keyset pagination, for the list routes that take it.
 *
 * Distinct from PageParams above, which takes a real number and so cannot
 * parse `?limit=50` — everything on the wire is a string. The bounds match
 * apps/api/src/utils/pagination.ts (which clamps rather than rejects, so a
 * caller asking for 500 gets 200 rather than a 400) and ListOperationsQuery,
 * so one cursor and one limit mean the same thing on every list route.
 *
 * `cursor` is opaque: clients copy back the previous response's `next_cursor`
 * and never construct one. An unrecognisable value is treated as no cursor.
 */
export const CursorPageQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type CursorPageQuery = z.infer<typeof CursorPageQuery>;

export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    next_cursor: z.string().nullable(),
  });
}
